import React, { useEffect, useState } from "react";
import {
  Box, Checkbox, Collapse, FormControlLabel, IconButton, MenuItem, Stack,
  Switch, TextField, Tooltip, Typography, Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { LegacyTokens, TypeScale } from "../../../../theme/b13Tokens";
import { B13, B13Ink, tint12 } from "../../../../theme/b13Tokens";
import { resolvePhaseIcon } from "../../../../Components/IconPicker/IconPicker";
import EditPhaseModal, { EditPhaseValue } from "./EditPhaseModal";
import { MIN_ACCURACY_NON_STARTING, AMBER_WARN_BELOW } from "./workflowConstants";
import MaintenanceIntervalRows, { IntervalRow } from "./MaintenanceIntervalRows";
import { TargetStatusChip } from "./targetStatusTone";
import {
  getEffectivePhaseDestination,
  getStandardPhaseType,
  isMaintenanceCapablePhase,
} from "./phasePredicates";
import {
  useListPhaseIntervalsQuery,
  useCreatePhaseIntervalMutation,
  useUpdatePhaseIntervalMutation,
  useDeletePhaseIntervalMutation,
} from "../../../../Api/attoDeskApi";
import {
  objectiveFamily,
  durationAggregation,
  isReduceObjective,
} from "./workflowObjectives";

// Duration criterion label — mirrors the aggregation slice in the
// backend server-authoritative mapping. "Reduce" flips Min→Max in the
// leading modifier; the trailing suffix (Total/Min/Max/Average) still
// names which aggregation the value is compared against.
function durationCriterionLabel(
  slice: "total" | "avg" | "min" | "max" | null,
  reduce: boolean,
): string {
  const head = reduce ? "Max." : "Min.";
  switch (slice) {
    case "total": return `${head} Total Duration to master`;
    case "avg":   return `${head} Average Duration to master`;
    case "min":   return `${head} Minimum Duration to master`;
    case "max":   return `${head} Maximum Duration to master`;
    default:      return `${head} Duration to master`;
  }
}

/*
 * Inline numeric input with a local draft-string state so the user
 * can temporarily clear the field while editing. The prior pattern —
 * `onChange={e => onCommit(Math.max(1, Number(e.target.value)))}` —
 * coerced an empty string to `1` on every keystroke, so Backspace
 * from "1" immediately became "1" again and the field could never
 * be cleared to type a replacement.
 *
 * Behaviour:
 *   - typing → local draft only, NEVER commits partial values
 *   - blur   → parse and clamp; if empty/invalid → restore the
 *              previous committed value (no silent 0/1)
 *   - external prop change → syncs down (Save round-trip, template
 *     swap, phase duplicate, etc.)
 *   - `min`, `max` clamps applied on commit (not per keystroke), so
 *     the display can hold intermediate strings like "" or "2" while
 *     the user is on the way to "20".
 *
 * Uses type="text" + inputMode="numeric" to (a) side-step the native
 * spinner arrows and (b) let the field carry an empty string
 * legitimately (a controlled type="number" clamps display too).
 * Mouse-wheel is blurred on the field only — no page listeners, so
 * the previous zoom-hijack regression cannot recur.
 */
function InlinePhaseNumberInput(props: {
  value: number;
  min: number;
  max?: number;
  onCommit: (n: number) => void;
  disabled?: boolean;
  error?: boolean;
  width?: number;
  variantSx?: any;
}) {
  const { value, min, max, onCommit, disabled, error, width = 56 } = props;
  const [draft, setDraft] = useState<string>(String(value ?? ""));

  // Sync from prop when the committed value changes underneath us
  // (external Save, template swap, etc.). Not while the user is
  // typing — this fires on prop identity, not keystrokes.
  useEffect(() => { setDraft(String(value ?? "")); }, [value]);

  const commit = () => {
    if (draft.trim() === "") {
      setDraft(String(value));
      return;
    }
    const parsed = Number(draft);
    if (!Number.isFinite(parsed)) { setDraft(String(value)); return; }
    let clamped = parsed;
    if (typeof min === "number") clamped = Math.max(min, clamped);
    if (typeof max === "number") clamped = Math.min(max, clamped);
    setDraft(String(clamped));
    if (clamped !== value) onCommit(clamped);
  };

  // Prior bug: with variant="standard", the input's underline extended
  // across the TextField ROOT, whose flex width could reach beyond the
  // small (48–56px) input pane. Clicking in that outer strip focused
  // the input with the caret at the right edge — right next to the
  // static suffix "session" / "trial" — making it look as though the
  // suffix itself was editable. Switching to variant="outlined" plus
  // an explicit ROOT width restricts the hitbox to the visible box.
  // Center-aligning the caret also moves it away from the suffix.
  return (
    <TextField
      size="small"
      type="text"
      inputMode="numeric"
      value={draft}
      disabled={disabled}
      inputProps={{
        style: { textAlign: "center", padding: "6px 8px", width: "100%" },
        "aria-valuemin": min,
        "aria-valuemax": max,
        pattern: "[0-9]*",
      }}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === "" || /^[0-9]+$/.test(raw)) setDraft(raw);
      }}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { commit(); (e.target as HTMLElement).blur(); } }}
      onWheel={(e) => (e.currentTarget.querySelector("input") as HTMLInputElement | null)?.blur()}
      error={error}
      variant="outlined"
      sx={{
        // Explicit ROOT width so the visible box matches the number,
        // and clicks outside the box hit the surrounding Stack (not
        // the input). Fixed width, not min-width, so the suffix
        // cannot creep leftward under the input's hit area.
        width: `${Math.max(width, 52)}px`,
        flex: "0 0 auto",
        "& .MuiOutlinedInput-root": {
          borderRadius: 1,
        },
        "& .MuiOutlinedInput-input": {
          fontVariantNumeric: "tabular-nums",
        },
        // Hide the browser number spinner if the field ever falls back
        // to type="number" (defense-in-depth — we already use type="text").
        "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button": {
          WebkitAppearance: "none",
          margin: 0,
        },
        ...(props.variantSx || {}),
      }}
    />
  );
}

// Batch 7C Step 2 — a phase reads as "maintenance-type" when its name
// (case-insensitive, trimmed) is exactly "Maintenance". The workflow
// editor has no schema signal (both Mastered and Maintenance seed as
// isTerminal), and the spec §7C.1 anchors the rows to the terminal
// phase named "Maintenance" specifically. When the phase-status model
// gains a first-class "maintenance-type" flag (planned for 7C-Part-2)
// this predicate is the one call site to update.
function isMaintenanceTypePhase(name: string | undefined): boolean {
  return (name ?? "").trim().toLowerCase() === "maintenance";
}

// Batch 7 §7.3 — one bordered card per phase.
//
// HEADER (visible always):
//   [phase chip: colour + icon + label + pencil] → [resulting-status chip]
//
// BODY (visible always):
//   Min. Percent Correct to master  [N]%  across  [N]  session(s)
//   Min. number of trials required (per session)  [N] trial(s)
//
// ADVANCED SETTINGS (collapsed by default on every phase including #1):
//   Description, Starting/Final/Active, Required Prompt Level,
//   Supervisor Approval Required, Next Phase + Criteria, Move Back When,
//   live SUMMARY line (regenerates on every keystroke), reorder,
//   duplicate, delete.

export type PhaseCardValue = {
  id?: number;
  phaseName: string;
  colour: string;
  icon: string | null;
  description: string;
  entryRule: string;
  advancementRule: string;
  regressionRule: string;
  transitionsTo: string;              // legacy: phase advanced to when mastery criteria are met (fallback when no masteredTargetStatus)
  masteredTargetStatus: string;       // canonical target status to apply on mastery — "if MASTERED".
                                      // "" means "no explicit outcome — advance via transitionsTo like legacy phases."
  notMasteredTransitionsTo: string;   // Aug 2026 Path B — phase advanced to when the FULL evaluation window
                                      // completes and criteria are NOT met. "" = legacy behaviour (stay in phase).
  minPercentCorrect: number;
  acrossSessions: number;
  minTrialsPerSession: number;
  promptRequirement: string;
  manualApprovalRequired: boolean;
  isStarting: boolean;
  isTerminal: boolean;
  isActive: boolean;
  countsTowardAutoOpenLimit: boolean;   // Batch 17
  // T63 Part O — HiRasmus Probe behaviour flags. Both default false;
  // hydrated from mastery_workflow_phases columns of the same name.
  onlyProbeFirst?: boolean;
  autoAddPhaseLineOnEnter?: boolean;
  // T63 Part O — Frequency-family criterion. Nullable — legacy percent
  // workflows leave these unset and use minPercentCorrect. Frontend
  // only writes `thresholdValue`; the metric metadata (metricType /
  // comparisonOperator / thresholdUnit / aggregationType) is
  // server-derived from the workflow's objective_key.
  thresholdValue?: number | null;
  metricType?: string | null;
  comparisonOperator?: string | null;
  orderNumber: number;
  // Drag-reorder client key — assigned by WorkflowEditorBody, never
  // persisted. Required for @dnd-kit's SortableContext id and the
  // React key on the mapped card. Both consumers whitelist their
  // save payload, so this field never reaches the API.
  _dndId?: string;
};

export type PhaseCardProps = {
  value: PhaseCardValue;
  index: number;
  total: number;
  siblingPhaseNames: string[];        // names of ACTIVE named phases other than this one
  /**
   * Batch 7 §2 fix — icon + colour lookup by phase name for the
   * resulting-status chip. Includes every named phase in the workflow
   * (self included; the chip cannot ever point at self via transitionsTo,
   * but keeping the map complete is cheaper than filtering). The chip
   * MUST NOT string-match on the phase name to pick an icon; it looks up
   * transitionsTo in this map instead.
   */
  phasesByName: Record<string, { icon: string | null; colour: string }>;
  onChange: (patch: Partial<PhaseCardValue>) => void;
  onEditPhase: (next: EditPhaseValue) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canRemove: boolean;
  removeBlockedReason?: string | null;
  minAccuracyError?: string | null;
  minAccuracyWarn?: string | null;
  readOnly?: boolean;
  // dnd-kit sortable id. When omitted the card renders without drag
  // (previews, tests). WorkflowEditorBody passes value._dndId.
  sortableId?: string;
  // T63 Part O — canonical objective slug so the criterion row can
  // render Frequency-shaped controls when the workflow's objective
  // family is Frequency. Null / percent keys → legacy Percent-Correct
  // controls.
  objectiveKey?: string | null;
};

function ruleSummary(v: PhaseCardValue): string {
  const s = v.acrossSessions ?? 1;
  const t = v.minTrialsPerSession ?? 1;
  const acc = `${v.minPercentCorrect}% or higher accuracy and ${t} trial${t === 1 ? "" : "s"} per session across ${s} consecutive session${s === 1 ? "" : "s"}`;

  // Aug 2026 — read the two-branch outcome so the summary reflects
  // the explicit mastery outcome as well as the not-mastered path.
  const mastered = (v.masteredTargetStatus || "").trim();
  const notMastered = (v.transitionsTo || "").trim();

  if (mastered) {
    const label = mastered.charAt(0).toUpperCase() + mastered.slice(1);
    if (v.isTerminal) {
      return `On mastery (${acc}), the target will be marked ${label} and the workflow will end.`;
    }
    if (notMastered) {
      return `If ${acc} the target will be marked ${label}. Otherwise it will move to ${notMastered}.`;
    }
    return `On mastery (${acc}), the target will be marked ${label}.`;
  }
  const to = notMastered ? `to ${notMastered}` : "forward";
  return `Move ${to} after ${s} consecutive session${s === 1 ? "" : "s"} with ${v.minPercentCorrect}% or higher accuracy and ${t} trial${t === 1 ? "" : "s"} per session.`;
}

export default function PhaseCard({
  value, index, total, siblingPhaseNames, phasesByName,
  onChange, onEditPhase, onRemove, onDuplicate, onMoveUp, onMoveDown,
  canRemove, removeBlockedReason,
  minAccuracyError, minAccuracyWarn,
  readOnly = false,
  sortableId,
  objectiveKey,
}: PhaseCardProps) {
  // T63 Part O — Frequency-family criterion shape. Increase/Reduce
  // frequency both use a count threshold; the direction only decides
  // whether the label reads "Min." (>=) or "Max." (<=). The metadata
  // itself is server-derived from objective_key — we mirror the
  // display-side inference here so the label is honest even before
  // the first save.
  // Family-aware criterion dispatch. Mirrors backend
  // MasteryWorkflowController::deriveCriterionMetadata so labels/units
  // stay honest before the first save.
  const family = objectiveFamily(objectiveKey);
  const isFrequency = family === "frequency";
  const isRate      = family === "rate";
  const isDuration  = family === "duration";
  const isManual    = family === "manual";
  const isReduce    = isReduceObjective(objectiveKey);
  const durationSlice = durationAggregation(objectiveKey); // total|avg|min|max|null
  const isReduceFrequency = objectiveKey === "reduce_frequency";
  // §7.3 — Advanced settings collapsed on open for every phase, incl #1.
  const [advOpen, setAdvOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const PhaseIc = resolvePhaseIcon(value.icon);

  // useSortable is called unconditionally (rules-of-hooks); returned
  // bindings are ignored when sortableId is absent.
  const sortable = useSortable({ id: sortableId ?? `phase-${index}`, disabled: readOnly });
  const dragActive = Boolean(sortableId) && sortable.isDragging;
  const dragStyle: React.CSSProperties = sortableId
    ? {
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        opacity: dragActive ? 0.7 : undefined,
        zIndex: dragActive ? 2 : undefined,
        boxShadow: dragActive ? "0 8px 20px rgba(0,0,0,0.12)" : undefined,
      }
    : {};

  // Batch 7C Step 2 — fetch intervals only when this is a maintenance-
  // type phase AND we have a saved phase id (a not-yet-saved phase has
  // no id to query against; rows appear after the first save).
  // T63 Part O §H — Mastered is HiRasmus's maintenance phase, so its
  // card also renders the interval stack + fetches phase_intervals.
  const isMaint = isMaintenanceCapablePhase(value.phaseName);
  const stdType = getStandardPhaseType(value.phaseName);
  const dest = getEffectivePhaseDestination(value);
  const canFetchIntervals = isMaint && typeof value.id === "number";
  const { data: intervalsResp } = useListPhaseIntervalsQuery(value.id as number, {
    skip: !canFetchIntervals,
  });
  const [createInterval] = useCreatePhaseIntervalMutation();
  const [updateInterval] = useUpdatePhaseIntervalMutation();
  const [deleteInterval] = useDeletePhaseIntervalMutation();

  const intervalRows: IntervalRow[] = React.useMemo(() => {
    const raw = (intervalsResp as any)?.data ?? [];
    return Array.isArray(raw) ? raw.map((r: any): IntervalRow => ({
      id: Number(r.id),
      frequency: r.frequency,
      forCount: r.forCount === null ? null : Number(r.forCount),
      forUnit: r.forUnit,
      orderNumber: Number(r.orderNumber ?? 0),
    })) : [];
  }, [intervalsResp]);

  // Batch 7 §2 fix — resulting-status chip does NOT match on phase names.
  // Icon and colour come from whichever phase transitionsTo resolves to.
  // Terminal phases with no onward transition get a generic check icon.
  // A transitionsTo that doesn't resolve (e.g. dangling reference after
  // an out-of-band rename) falls back to the default icon and the raw
  // transitionsTo string as the label.
  const nextPhase = value.transitionsTo ? phasesByName[value.transitionsTo] : undefined;
  const rsLabel = value.transitionsTo || (value.isTerminal ? "End" : "Next");
  const RsIcon = value.transitionsTo
    ? resolvePhaseIcon(nextPhase?.icon ?? null)
    : (value.isTerminal ? CheckIcon : ChevronRightIcon);
  const rsIconColor = value.transitionsTo
    ? (nextPhase?.colour || LegacyTokens.emerald)
    : (value.isTerminal ? LegacyTokens.emerald : LegacyTokens.ink3);

  return (
    <Box
      ref={sortableId ? sortable.setNodeRef : undefined}
      style={dragStyle}
      data-testid="phase-card"
      sx={{
        border: `1px solid ${dragActive ? B13.primary : LegacyTokens.line}`,
        borderRadius: `${LegacyTokens.radius.card}px`,
        backgroundColor: LegacyTokens.surface,
        p: 2,
        opacity: value.isActive ? 1 : 0.6,
      }}
    >
      {/* Header row — drag handle → phase chip → resulting-status chip.
          Handle sits first, OUTSIDE the coloured phase chip, so dragging
          from the chip does nothing (no listeners on the chip Box). The
          chip's edit-pencil continues to open EditPhaseModal on click.
          Listeners live only on the handle IconButton via
          setActivatorNodeRef. */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        {sortableId && (
          <Tooltip title="Drag to reorder (Space to pick up, arrows to move, Space to drop)" arrow>
            <span>
              <IconButton
                size="small"
                aria-label={`Reorder phase ${index + 1}`}
                disabled={readOnly}
                ref={sortable.setActivatorNodeRef}
                {...sortable.attributes}
                {...sortable.listeners}
                sx={{
                  color: LegacyTokens.ink3,
                  cursor: dragActive ? "grabbing" : "grab",
                  touchAction: "none",
                  "&:hover": { color: B13.primary },
                }}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Box
          sx={{
            display: "inline-flex", alignItems: "center", gap: 0.75,
            px: 1.25, py: 0.5,
            borderRadius: `${LegacyTokens.radius.chip}px`,
            backgroundColor: value.colour || LegacyTokens.blue,
            color: LegacyTokens.surface,
          }}
        >
          <PhaseIc sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            {value.phaseName || "Unnamed phase"}
          </Typography>
          <Tooltip title="Edit phase" arrow>
            <IconButton
              size="small"
              disabled={readOnly}
              onClick={() => setEditOpen(true)}
              aria-label="Edit phase"
              sx={{ color: LegacyTokens.surface, p: 0.25, ml: 0.25 }}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <ArrowForwardIcon sx={{ color: LegacyTokens.ink3, fontSize: 16 }} />

        {/* T63 Part O §C/D/Q — ONE effective destination chip only.
            Precedence lives in getEffectivePhaseDestination:
              1. masteredTargetStatus → target lifecycle status wins
                 (e.g. Probe with Closed shows "Closed", not
                 "Mastered → Closed").
              2. transitionsTo → "On mastery → <next phase>".
              3. isTerminal → "End" pill.
              4. none. */}
        {dest.kind === "target_status" && (
          <Tooltip
            title="Applied when mastery criteria are met. The target's lifecycle status changes; no further phase progression."
            arrow
          >
            {/* T63 Part O §C/I — single target-status chip. The prior
                MasteredOutcomeChips rendered a mint "Mastered" pill
                *and* the status chip, so Probe (target status = Closed)
                looked like "Probe → Mastered + Closed". The header
                should show the FINAL effective destination only. */}
            <Box sx={{ display: "inline-flex" }}>
              <TargetStatusChip status={dest.value} />
            </Box>
          </Tooltip>
        )}
        {dest.kind === "next_phase" && (
          <Tooltip title="The phase the target advances to when mastery criteria are met." arrow>
            <Box
              sx={{
                display: "inline-flex", alignItems: "center", gap: 0.5,
                px: 1, py: 0.3,
                borderRadius: `${LegacyTokens.radius.chip}px`,
                backgroundColor: LegacyTokens.canvas2,
                color: LegacyTokens.ink2,
              }}
            >
              <RsIcon sx={{ fontSize: 14, color: rsIconColor }} />
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                {`On mastery → ${rsLabel}`}
              </Typography>
            </Box>
          </Tooltip>
        )}
        {dest.kind === "terminal_end" && (
          <Tooltip title="Final phase — no further progression." arrow>
            <Box
              sx={{
                display: "inline-flex", alignItems: "center", gap: 0.5,
                px: 1, py: 0.3,
                borderRadius: `${LegacyTokens.radius.chip}px`,
                backgroundColor: LegacyTokens.canvas2,
                color: LegacyTokens.ink2,
              }}
            >
              <CheckIcon sx={{ fontSize: 14, color: LegacyTokens.emerald }} />
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>End</Typography>
            </Box>
          </Tooltip>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {value.isStarting && (
          <Box sx={{
            fontSize: 10, fontWeight: 700, letterSpacing: 0.4, px: 1, py: 0.25,
            borderRadius: `${LegacyTokens.radius.chip}px`,
            backgroundColor: tint12(B13.primary), color: B13.primary,
          }}>START</Box>
        )}
        {/* Aug 2026: FINAL badge companion so the workflow's terminal
            phase is instantly identifiable in the collapsed card header. */}
        {value.isTerminal && (
          <Box sx={{
            fontSize: 10, fontWeight: 700, letterSpacing: 0.4, px: 1, py: 0.25,
            borderRadius: `${LegacyTokens.radius.chip}px`,
            backgroundColor: tint12(LegacyTokens.emerald), color: LegacyTokens.emerald,
          }}>FINAL</Box>
        )}
      </Stack>

      {/* Batch 7C Step 2 — the interval stack sits ABOVE the accuracy
          row on maintenance-type phases, replacing the "across N
          sessions" affordance (which does not apply once the target
          is in maintenance — cadence, not consecutive-session count,
          is the readiness signal). The "+ Add maintenance interval"
          button is disabled visually only if the phase has no id yet
          (unsaved); see MaintenanceIntervalRows readOnly prop. */}
      {isMaint && (
        <Box sx={{ mb: 2 }}>
          <MaintenanceIntervalRows
            rows={intervalRows}
            readOnly={readOnly || !canFetchIntervals}
            onAdd={async (frequency) => {
              if (!canFetchIntervals) return;
              await createInterval({ phaseId: value.id, frequency });
            }}
            onUpdate={async (id, patch) => {
              await updateInterval({ id, ...patch });
            }}
            onDelete={async (id) => {
              await deleteInterval(id);
            }}
          />
        </Box>
      )}

      {/* Body — two visible rows */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={7}>
          {isManual ? (
            /* Manual Mastery — no automatic threshold. Evaluator gate
               is enforced server-side (SessionDataController skips
               metricType='manual'). Copy tells the clinician the phase
               only advances via an authorized manual action. */
            <Stack direction="row" spacing={1} alignItems="center" data-testid="manual-mastery-explainer">
              <CheckIcon sx={{ fontSize: 16, color: LegacyTokens.ink3 }} />
              <Typography sx={{ fontSize: 13, color: LegacyTokens.ink2 }}>
                This phase is advanced manually by an authorized staff member.
              </Typography>
            </Stack>
          ) : isRate ? (
            /* Rate criterion — dispatch on the Rate/hour vs Rate/min
               objective key so the suffix matches the persisted
               aggregationType. Rate/hour additionally requires that
               the recorded session carry an authoritative measured
               observation window; that gate is enforced server-side
               in SessionDataController::checkMasteryCriteria. */
            <Stack direction="row" spacing={1.5} alignItems="center" data-testid="rate-criterion" data-rate-scale={(objectiveKey ?? "").includes("per_hour") ? "hour" : "minute"}>
              <Typography sx={{ fontSize: 13, color: LegacyTokens.ink2, flexShrink: 0 }}>
                {isReduce ? "Max. Rate to master" : "Min. Rate to master"}
              </Typography>
              <InlinePhaseNumberInput
                value={Number(value.thresholdValue ?? 0)}
                min={0}
                disabled={readOnly}
                onCommit={(n) => onChange({ thresholdValue: n })}
                width={64}
              />
              <Typography sx={{ fontSize: 13, color: LegacyTokens.ink3 }}>
                {(objectiveKey ?? "").includes("per_hour") ? "per hour  across" : "per minute  across"}
              </Typography>
              <InlinePhaseNumberInput
                value={value.acrossSessions}
                min={1}
                disabled={readOnly}
                onCommit={(n) => onChange({ acrossSessions: n })}
                width={48}
              />
              <Typography sx={{ fontSize: 13, color: LegacyTokens.ink3 }}>
                session{value.acrossSessions === 1 ? "" : "s"}
              </Typography>
            </Stack>
          ) : isDuration ? (
            /* Duration family — canonical unit seconds. Label
               reflects which aggregation slice this key targets. */
            <Stack direction="row" spacing={1.5} alignItems="center" data-testid="duration-criterion" data-duration-slice={durationSlice ?? ""}>
              <Typography sx={{ fontSize: 13, color: LegacyTokens.ink2, flexShrink: 0 }}>
                {durationCriterionLabel(durationSlice, isReduce)}
              </Typography>
              <InlinePhaseNumberInput
                value={Number(value.thresholdValue ?? 0)}
                min={0}
                disabled={readOnly}
                onCommit={(n) => onChange({ thresholdValue: n })}
                width={64}
              />
              <Typography sx={{ fontSize: 13, color: LegacyTokens.ink3 }}>seconds  across</Typography>
              <InlinePhaseNumberInput
                value={value.acrossSessions}
                min={1}
                disabled={readOnly}
                onCommit={(n) => onChange({ acrossSessions: n })}
                width={48}
              />
              <Typography sx={{ fontSize: 13, color: LegacyTokens.ink3 }}>
                session{value.acrossSessions === 1 ? "" : "s"}
              </Typography>
            </Stack>
          ) : isFrequency ? (
            /* T63 Part O — Frequency-family criterion. Reads/writes
               thresholdValue (count). No `%` suffix. Label reads
               "Max." for Reduce Frequency, "Min." for Increase. */
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography sx={{ fontSize: 13, color: LegacyTokens.ink2, flexShrink: 0 }}>
                {isReduceFrequency ? "Max. Frequency to master" : "Min. Frequency to master"}
              </Typography>
              <InlinePhaseNumberInput
                value={Number(value.thresholdValue ?? 0)}
                min={0}
                disabled={readOnly}
                onCommit={(n) => onChange({ thresholdValue: n })}
                width={64}
              />
              <Typography sx={{ fontSize: 13, color: LegacyTokens.ink3 }}>occurrences  across</Typography>
              <InlinePhaseNumberInput
                value={value.acrossSessions}
                min={1}
                disabled={readOnly}
                onCommit={(n) => onChange({ acrossSessions: n })}
                width={48}
              />
              <Typography sx={{ fontSize: 13, color: LegacyTokens.ink3 }}>
                session{value.acrossSessions === 1 ? "" : "s"}
              </Typography>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography sx={{ fontSize: 13, color: LegacyTokens.ink2, flexShrink: 0 }}>
                Min. Percent Correct to master
              </Typography>
              <InlinePhaseNumberInput
                value={value.minPercentCorrect}
                min={0}
                max={100}
                disabled={readOnly}
                error={Boolean(minAccuracyError)}
                onCommit={(n) => onChange({ minPercentCorrect: n })}
                width={56}
                variantSx={{
                  "& .MuiInput-underline:after": { borderBottomColor: minAccuracyError ? LegacyTokens.red : B13.primary },
                }}
              />
              {isMaint ? (
                <Typography sx={{ fontSize: 13, color: LegacyTokens.ink3 }}>%</Typography>
              ) : (
                <>
                  <Typography sx={{ fontSize: 13, color: LegacyTokens.ink3 }}>%  across</Typography>
                  <InlinePhaseNumberInput
                    value={value.acrossSessions}
                    min={1}
                    disabled={readOnly}
                    onCommit={(n) => onChange({ acrossSessions: n })}
                    width={48}
                  />
                  <Typography sx={{ fontSize: 13, color: LegacyTokens.ink3 }}>
                    session{value.acrossSessions === 1 ? "" : "s"}
                  </Typography>
                </>
              )}
            </Stack>
          )}
          {/* Accuracy warnings only apply to the Percent-Correct
              criterion — suppress for Frequency/Rate/Duration/Manual
              which do not read minPercentCorrect. */}
          {!isFrequency && !isRate && !isDuration && !isManual && minAccuracyError && (
            <Typography sx={{ fontSize: 11, color: LegacyTokens.red, mt: 0.5 }}>
              {minAccuracyError}
            </Typography>
          )}
          {!isFrequency && !isRate && !isDuration && !isManual && !minAccuracyError && minAccuracyWarn && (
            <Typography sx={{ fontSize: 11, color: LegacyTokens.amberText, mt: 0.5 }}>
              {minAccuracyWarn}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={5}>
          {isManual ? null : (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography sx={{ fontSize: 13, color: LegacyTokens.ink2, flexShrink: 0 }}>
                {(isFrequency || isRate) ? "Min. observations per session"
                 : isDuration ? "Min. episodes per session"
                 : "Min. trials per session"}
              </Typography>
              <InlinePhaseNumberInput
                value={value.minTrialsPerSession}
                min={1}
                disabled={readOnly}
                onCommit={(n) => onChange({ minTrialsPerSession: n })}
                width={48}
              />
              <Typography sx={{ fontSize: 13, color: LegacyTokens.ink3 }}>
                {(isFrequency || isRate)
                  ? `observation${value.minTrialsPerSession === 1 ? "" : "s"}`
                  : isDuration
                    ? `episode${value.minTrialsPerSession === 1 ? "" : "s"}`
                    : `trial${value.minTrialsPerSession === 1 ? "" : "s"}`}
              </Typography>
            </Stack>
          )}
        </Grid>
      </Grid>

      {/* Advanced settings — collapsed on open for every phase (§7.3) */}
      <Box
        onClick={() => setAdvOpen((v) => !v)}
        role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setAdvOpen((v) => !v); } }}
        sx={{
          mt: 2, display: "flex", alignItems: "center", gap: 0.5,
          fontSize: 12, fontWeight: 600, color: LegacyTokens.ink3,
          cursor: "pointer", userSelect: "none",
          "&:hover": { color: B13.primary },
        }}
      >
        {advOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        Advanced settings
      </Box>

      <Collapse in={advOpen} unmountOnExit>
        <Box sx={{ mt: 1.5, pl: 3 }}>
          <Grid container spacing={2} columnSpacing={2.5}>
            {/* T63 Part O §L — Description hidden from the standard
                phase UI globally. Existing saved values ride through
                in `value.description` unmodified (see Editor save
                payload — Part V/W). Custom (non-standard-named)
                phases fall through to the legacy expert block below. */}

            {/* T63 Part O §A/B/L — Standard clinical view for the
                HiRasmus-shaped phases. Only the settings that operators
                need to configure clinical progression are exposed here;
                internal graph metadata (isStarting/isTerminal/isActive/
                countsTowardAutoOpenLimit), description, prompt-level
                requirement, supervisor approval, free-text
                advancement/regression rules, and legacy transitionsTo
                are hidden. Their saved values are preserved in state
                and re-sent verbatim on save. */}
            {stdType === "probe" && (
              <>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6, mb: 0.75 }}>
                    Probe outcome
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select fullWidth size="small"
                    label="Status after probe (if not mastered)"
                    value={value.notMasteredTransitionsTo || ""}
                    disabled={readOnly}
                    onChange={(e) => onChange({ notMasteredTransitionsTo: e.target.value })}
                    SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                    helperText=" "
                  >
                    <MenuItem value=""><em>Stay in Probe</em></MenuItem>
                    {siblingPhaseNames
                      .filter((n) => n !== value.phaseName)
                      .map((n) => (<MenuItem key={n} value={n}>{n}</MenuItem>))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select fullWidth size="small"
                    label="Target status if mastered"
                    value={value.masteredTargetStatus || ""}
                    disabled={readOnly}
                    onChange={(e) => onChange({ masteredTargetStatus: e.target.value })}
                    SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                    helperText=" "
                  >
                    <MenuItem value=""><em>Use next phase</em></MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="maintenance">Maintenance</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                    <MenuItem value="discontinued">Discontinued</MenuItem>
                  </TextField>
                </Grid>

                {/* T63 Part O §9 — Probe behavior. Two additive booleans
                    backed by real backend columns (mastery_workflow_phases
                    .onlyProbeFirst / .autoAddPhaseLineOnEnter). Runtime
                    enforcement lives in SessionDataController + the
                    TargetPhaseEntryService — this UI is not a stub. */}
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6, mt: 1, mb: 0.75 }}>
                    Probe behavior
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={<Switch size="small" checked={!!value.onlyProbeFirst}
                      onChange={(e) => onChange({ onlyProbeFirst: e.target.checked })} disabled={readOnly} />}
                    label={<Typography sx={{ fontSize: 13 }}>Only probe first</Typography>}
                  />
                  <Typography sx={{ fontSize: 11, color: LegacyTokens.ink3, mt: 0.25, ml: 5 }}>
                    Skip Probe on subsequent workflow entries once the target has completed Probe once.
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={<Switch size="small" checked={!!value.autoAddPhaseLineOnEnter}
                      onChange={(e) => onChange({ autoAddPhaseLineOnEnter: e.target.checked })} disabled={readOnly} />}
                    label={<Typography sx={{ fontSize: 13 }}>Automatically add phase line when entering ‘Probe’ status</Typography>}
                  />
                  <Typography sx={{ fontSize: 11, color: LegacyTokens.ink3, mt: 0.25, ml: 5 }}>
                    Adds a Probe marker to the target’s phase-history timeline on entry.
                  </Typography>
                </Grid>
              </>
            )}

            {stdType === "acquisition" && (
              <>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6, mb: 0.75 }}>
                    Progression
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select fullWidth size="small"
                    label="On mastery — next phase"
                    value={value.transitionsTo || ""}
                    disabled={readOnly}
                    onChange={(e) => onChange({ transitionsTo: e.target.value })}
                    SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                    helperText=" "
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {siblingPhaseNames
                      .filter((n) => n !== value.phaseName)
                      .map((n) => (<MenuItem key={n} value={n}>{n}</MenuItem>))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    sx={{ m: 0, mt: 0.5 }}
                    control={<Switch size="small" checked={value.manualApprovalRequired}
                      onChange={(e) => onChange({ manualApprovalRequired: e.target.checked })} disabled={readOnly} />}
                    label={<Typography sx={{ fontSize: 13 }}>Supervisor approval required on mastery</Typography>}
                  />
                </Grid>
              </>
            )}

            {stdType === "mastered" && (
              <>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6, mb: 0.75 }}>
                    Maintenance outcome
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select fullWidth size="small"
                    label="If maintenance criteria are not met"
                    value={value.notMasteredTransitionsTo || ""}
                    disabled={readOnly}
                    onChange={(e) => onChange({ notMasteredTransitionsTo: e.target.value })}
                    SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                    helperText=" "
                  >
                    <MenuItem value=""><em>Stay in Mastered</em></MenuItem>
                    {siblingPhaseNames
                      .filter((n) => n !== value.phaseName)
                      .map((n) => (<MenuItem key={n} value={n}>{n}</MenuItem>))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select fullWidth size="small"
                    label="Target status if maintenance completes"
                    value={value.masteredTargetStatus || ""}
                    disabled={readOnly}
                    onChange={(e) => onChange({ masteredTargetStatus: e.target.value })}
                    SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                    helperText=" "
                  >
                    <MenuItem value=""><em>Keep target open</em></MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="maintenance">Maintenance</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                    <MenuItem value="discontinued">Discontinued</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}

            {/* Custom (non-standard-named) phase — legacy expert form.
                Preserved verbatim so bespoke workflows keep their full
                configurability. */}
            {stdType === "custom" && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small" label="Description"
                    value={value.description}
                    disabled={readOnly}
                    onChange={(e) => onChange({ description: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6, mb: 0.75 }}>
                    Workflow Position
                  </Typography>
                  <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                    <Tooltip title="Where a new target begins in this workflow. Exactly one phase per workflow." arrow>
                      <FormControlLabel
                        control={<Switch size="small" checked={value.isStarting}
                          onChange={(e) => onChange({ isStarting: e.target.checked })} disabled={readOnly} />}
                        label={<Typography sx={{ fontSize: 13 }}>Starting Phase</Typography>}
                      />
                    </Tooltip>
                    <Tooltip title="Mastering this phase completes the workflow. Exactly one per workflow. A final phase cannot have a Next Phase." arrow>
                      <FormControlLabel
                        control={<Switch size="small" checked={value.isTerminal}
                          onChange={(e) => onChange({ isTerminal: e.target.checked })} disabled={readOnly} />}
                        label={<Typography sx={{ fontSize: 13 }}>Final Phase</Typography>}
                      />
                    </Tooltip>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6, mb: 0.75 }}>
                    Availability
                  </Typography>
                  <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                    <Tooltip title="Whether this phase is available for use. Inactive phases should not be starting, final, or selected as a next phase." arrow>
                      <FormControlLabel
                        control={<Switch size="small" checked={value.isActive}
                          onChange={(e) => onChange({ isActive: e.target.checked })} disabled={readOnly} />}
                        label={<Typography sx={{ fontSize: 13 }}>Active</Typography>}
                      />
                    </Tooltip>
                    <Tooltip title="Whether targets in this phase count toward the program's auto-open target limit." arrow>
                      <FormControlLabel
                        control={<Switch size="small" checked={value.countsTowardAutoOpenLimit}
                          onChange={(e) => onChange({ countsTowardAutoOpenLimit: e.target.checked })} disabled={readOnly} />}
                        label={<Typography sx={{ fontSize: 13 }}>Counts toward auto-open limit</Typography>}
                      />
                    </Tooltip>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select fullWidth size="small" label="Required Prompt Level"
                    value={value.promptRequirement || "any"}
                    disabled={readOnly}
                    onChange={(e) => onChange({ promptRequirement: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="any">Any prompt</MenuItem>
                    <MenuItem value="independent_only">Independent only</MenuItem>
                    <MenuItem value="custom">Specific Prompt Level</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    sx={{ m: 0, mt: 1 }}
                    control={<Checkbox size="small" checked={value.manualApprovalRequired}
                      onChange={(e) => onChange({ manualApprovalRequired: e.target.checked })} />}
                    label={<Typography sx={{ fontSize: 13 }}>Supervisor Approval Required</Typography>}
                  />
                </Grid>
              </>
            )}

            {/* Legacy Progression block (transitionsTo / masteredTargetStatus /
                notMasteredTransitionsTo / free-text rules) — only rendered
                for custom phases. Standard phases (Probe/Acquisition/
                Mastered) show the HiRasmus-shaped controls above; their
                underlying data ships in the save payload unchanged. */}
            {stdType === "custom" && (<>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6, mb: 0.75 }}>
                Progression
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Tooltip title={value.isTerminal
                ? "This is the final phase — the workflow ends here."
                : "The phase the target advances to when mastery criteria for this phase are met. When a Mastered Target Status is also set, THAT status is applied instead and this Next Phase is ignored."} arrow>
                <TextField
                  select fullWidth size="small"
                  // Truthful wording (spec Core Rule §2 / Model A):
                  // the runtime advances via transitionsTo only when
                  // criteria ARE met AND no masteredTargetStatus is
                  // configured. The previous "If NOT mastered" wording
                  // lied about which branch fires — reverted here.
                  label={value.isTerminal ? "Workflow ends here" : "On Mastery — Next Phase"}
                  value={value.isTerminal ? "" : (value.transitionsTo || "")}
                  disabled={readOnly || value.isTerminal}
                  onChange={(e) => onChange({ transitionsTo: e.target.value })}
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                  helperText={value.isTerminal ? "Final phases have no next phase." : " "}
                >
                  <MenuItem value=""><em>None (final)</em></MenuItem>
                  {siblingPhaseNames
                    .filter((n) => n !== value.phaseName)
                    .map((n) => (
                      <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                </TextField>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Tooltip title="Status applied to the target when this phase's mastery criteria are successfully met. Leave blank to keep the legacy behaviour of advancing via the Next Phase." arrow>
                <TextField
                  select fullWidth size="small"
                  label="If MASTERED — Target Status"
                  value={value.masteredTargetStatus || ""}
                  disabled={readOnly}
                  onChange={(e) => onChange({ masteredTargetStatus: e.target.value })}
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                  helperText={value.masteredTargetStatus === "closed"
                    ? "Removes the target from active progression after mastery."
                    : (value.masteredTargetStatus ? " " : "Leave blank to advance via Next Phase.")}
                >
                  <MenuItem value=""><em>Use Next Phase</em></MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                  <MenuItem value="discontinued">Discontinued</MenuItem>
                </TextField>
              </Tooltip>
            </Grid>
            {/* Aug 2026 Path B — real failed-mastery branch.
                Backend runtime (SessionDataController::checkMasteryCriteria)
                fires this ONLY after the full evaluation window
                (`acrossSessions` sessions with `minTrialsPerSession`
                trials each) has completed AND criteria are not met.
                Blank preserves legacy behaviour (target stays in phase). */}
            <Grid item xs={12} sm={6}>
              <Tooltip title="Used only after the full configured evaluation window is complete and mastery criteria were not achieved. Leave blank to keep the target in this phase after failed evaluations." arrow>
                <TextField
                  select fullWidth size="small"
                  label={value.isTerminal ? "Workflow ends here" : "If NOT MASTERED — Next Phase"}
                  value={value.isTerminal ? "" : (value.notMasteredTransitionsTo || "")}
                  disabled={readOnly || value.isTerminal}
                  onChange={(e) => onChange({ notMasteredTransitionsTo: e.target.value })}
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                  helperText={value.isTerminal
                    ? "Final phases have no failed-mastery next phase."
                    : (value.notMasteredTransitionsTo
                      ? "Fires only after the full evaluation window completes without mastery."
                      : "Leave blank to keep the target in this phase on failed evaluations.")}
                >
                  <MenuItem value=""><em>None (stay in phase)</em></MenuItem>
                  {siblingPhaseNames
                    .filter((n) => n !== value.phaseName)
                    .map((n) => (
                      <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                </TextField>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Criteria to Move Forward"
                value={value.advancementRule}
                disabled={readOnly}
                onChange={(e) => onChange({ advancementRule: e.target.value })}
                helperText="Plain-language explanation for reports."
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Move back when (regression rule)"
                value={value.regressionRule}
                disabled={readOnly}
                onChange={(e) => onChange({ regressionRule: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              {/* SUMMARY — regenerates from live field values on every render
                 (which happens on every keystroke via parent state). It can
                 never disagree with the inputs — read it directly off value. */}
              <Box sx={{
                p: 1.25,
                borderRadius: `${LegacyTokens.radius.control}px`,
                backgroundColor: LegacyTokens.canvas2,
                borderLeft: `3px solid ${value.colour || LegacyTokens.blue}`,
              }}>
                <Typography sx={{ ...TypeScale.fieldLabel, mb: 0.25 }}>Summary</Typography>
                <Typography data-testid="phase-summary" sx={{ fontSize: 13, color: LegacyTokens.ink2 }}>
                  {ruleSummary(value)}
                </Typography>
              </Box>
            </Grid>
            </>)}

            <Grid item xs={12}>
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Tooltip title="Move up" arrow>
                  <span>
                    <IconButton size="small" disabled={readOnly || index === 0} onClick={onMoveUp} aria-label="Move up">
                      <KeyboardArrowUpIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move down" arrow>
                  <span>
                    <IconButton size="small" disabled={readOnly || index === total - 1} onClick={onMoveDown} aria-label="Move down">
                      <KeyboardArrowDownIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Duplicate" arrow>
                  <span>
                    <IconButton size="small" disabled={readOnly} onClick={onDuplicate} aria-label="Duplicate">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={canRemove ? "Delete" : (removeBlockedReason ?? "Cannot delete")} arrow>
                  <span>
                    <IconButton
                      size="small"
                      disabled={readOnly || !canRemove || total === 1}
                      onClick={onRemove}
                      aria-label="Delete"
                      sx={{ color: LegacyTokens.red, "&:hover": { backgroundColor: LegacyTokens.redSoft } }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      {/* Edit-phase modal (§7.4) */}
      <EditPhaseModal
        open={editOpen}
        value={{ phaseName: value.phaseName, colour: value.colour || LegacyTokens.blue, icon: value.icon }}
        onCancel={() => setEditOpen(false)}
        onSave={(next) => {
          onEditPhase(next);
          setEditOpen(false);
        }}
        onRemove={() => {
          onRemove();
          setEditOpen(false);
        }}
        canRemove={canRemove}
        removeBlockedReason={removeBlockedReason ?? null}
      />
    </Box>
  );
}
